import Decimal from "decimal.js";
import {
  WILL_BE_PAID_OFF,
  RESUBORDINATED,
  INCLUDE_IN_DTI
} from "../constants/account_dispositions";

import {
  total_net_yearly_income_loss_rental,
  total_payment
} from "./investment_reo";
import { total_monthly_payment } from "./loan";
import {
  debt_counted_in_invest_reo,
  second_home_reo_monthly_total
} from "./reo_dedupe";
import {
  UTILIZATION_LIABILITY_TYPES,
  PRIMARY_RESIDENCE,
  SECOND_HOME,
  SECOND_LOAN_SUB,
  HOME_EQUITY,
  MORTGAGE
} from "../constants/liability_types";
import {
  FIRST_POSITION,
  SECOND_POSITION,
  THIRD_POSITION
} from "../constants/lien_positions";

// Only revolving credit has utilization; a mortgage with a High Credit Limit
// entered must not be treated like a credit card (pay down to 50%).
function has_credit_utilization(accountSummary) {
  return UTILIZATION_LIABILITY_TYPES.includes(accountSummary.liability_type);
}
import { year_to_payment, compute_dti_income } from "./income";
import { JUMBO } from "../constants/loan_types";
import { summary_selected_loan } from "../constants/utils";
import { NO } from "../constants/yes_no";

export function compute_total(quantities) {
  return quantities.reduce((accumulator, balance) => {
    return accumulator.plus(new Decimal(balance));
  }, new Decimal(0));
}

export function percent_of_credit_used(accountSummary, residence) {
  if (!has_credit_utilization(accountSummary)) {
    return new Decimal(0);
  }
  const currentBalance = current_balance(accountSummary, residence);
  const highCreditLimit = new Decimal(accountSummary.high_credit_limit);
  if (currentBalance.greaterThan(0)) {
    return currentBalance.times(100).dividedToIntegerBy(highCreditLimit);
  } else {
    return new Decimal(0);
  }
}

export function lower_balance_payment_left(accountSummary, residence) {
  if (!has_credit_utilization(accountSummary)) {
    return new Decimal(0);
  }
  const currentBalance = current_balance(accountSummary, residence);
  const highCreditLimit = new Decimal(accountSummary.high_credit_limit);
  return highCreditLimit.dividedBy(2).minus(currentBalance);
}

export function lower_balance_payment_left_total(accountSummaries, residences) {
  const lowerBalancePaymentsLeft = accountSummaries.map(accountSummary => {
    return lower_balance_payment_left(
      accountSummary,
      residences[accountSummary.residence]
    );
  });
  return compute_total(lowerBalancePaymentsLeft);
}

export function monthly_payment(accountSummary, residence) {
  return residence
    ? residence_total_payment(
      residence.first_mortgage,
      residence.second_mortgage,
      residence.taxes,
      residence.insurance,
      residence.hoa
    )
    : new Decimal(accountSummary.monthly_payment);
}

export function monthly_payments_total(
  accountSummaries,
  residences,
  investmentProperties
) {
  const monthlyPayments = [];
  const netIncomeLoss = total_net_yearly_income_loss_rental(
    investmentProperties
  ).neg();
  if (netIncomeLoss.greaterThan(0)) {
    monthlyPayments.push(netIncomeLoss);
  }
  accountSummaries.forEach(accountSummary => {
    const residence = residences[accountSummary.residence];
    monthlyPayments.push(monthly_payment(accountSummary, residence));
  });
  return compute_total(monthlyPayments);
}

export function current_balance(accountSummary, residence) {
  return residence
    ? new Decimal(residence.principal_balance)
    : new Decimal(accountSummary.current_balance);
}

export function current_balance_total(accountSummaries, residences) {
  const currentBalances = accountSummaries.map(accountSummary => {
    return current_balance(
      accountSummary,
      residences[accountSummary.residence]
    );
  });
  return compute_total(currentBalances);
}

export function high_credit_limit_total(accountSummaries) {
  return compute_total(
    accountSummaries.map(accountSummary => accountSummary.high_credit_limit)
  );
}

export function payment_savings(accountSummary, residence, accountDisposition) {
  return accountDisposition === WILL_BE_PAID_OFF
    ? monthly_payment(accountSummary, residence)
    : new Decimal(0);
}

export function payment_savings_total(accountSummaries, residences, loan) {
  const paymentSavings = accountSummaries.map(accountSummary => {
    return payment_savings(
      accountSummary,
      residences[accountSummary.residence],
      (loan && loan.account_dispositions[accountSummary.id]) || INCLUDE_IN_DTI
    );
  });
  return compute_total(paymentSavings);
}

export function payment_for_DTI_calc(
  accountSummary,
  residence,
  accountDisposition
) {
  if (
    accountDisposition === RESUBORDINATED ||
    accountDisposition === INCLUDE_IN_DTI
  ) {
    return monthly_payment(accountSummary, residence);
  } else {
    return new Decimal(0);
  }
}

export function payment_for_DTI_calc_total(
  accountSummaries,
  residences,
  investmentProperties,
  loan
) {
  const paymentsForDTICalc = accountSummaries.map(accountSummary => {
    // A mortgage attached to a rental or second home whose payment Invest REO
    // already counts is not counted again (Invest REO is the one place for it).
    if (
      debt_counted_in_invest_reo(
        accountSummary,
        investmentProperties,
        residences[accountSummary.residence]
      )
    ) {
      return new Decimal(0);
    }
    return payment_for_DTI_calc(
      accountSummary,
      residences[accountSummary.residence],
      (loan && loan.account_dispositions[accountSummary.id]) || INCLUDE_IN_DTI
    );
  });
  // Rentals: only a net LOSS adds to debts (a positive net goes to income).
  const netIncomeLoss = total_net_yearly_income_loss_rental(
    investmentProperties
  ).neg();
  if (netIncomeLoss.greaterThan(0)) {
    paymentsForDTICalc.push(netIncomeLoss);
  }
  // Second homes on Invest REO: no rent, so the full monthly cost is a debt.
  paymentsForDTICalc.push(
    second_home_reo_monthly_total(investmentProperties, total_payment)
  );
  return compute_total(paymentsForDTICalc);
}

// TODO: why is accountDisposition here and unused?
export function balance_to_be_paid_down(
  accountSummary,
  residence //,
  // accountDisposition
) {
  const percentThreshold = 50;
  const percentOfCreditUsed = percent_of_credit_used(accountSummary, residence);
  if (percentOfCreditUsed.greaterThan(percentThreshold)) {
    return lower_balance_payment_left(accountSummary, residence);
  } else {
    return new Decimal(0);
  }
}

export function balance_to_be_paid_down_total(
  accountSummaries,
  residences,
  loan
) {
  const balancesToBePaidDown = accountSummaries.map(accountSummary => {
    return balance_to_be_paid_down(
      accountSummary,
      residences[accountSummary.residence],
      (loan && loan.account_dispositions[accountSummary.id]) || INCLUDE_IN_DTI
    );
  });
  return compute_total(balancesToBePaidDown);
}

export function balance_pay_off(current_balance, accountDisposition) {
  if (accountDisposition === WILL_BE_PAID_OFF) {
    return new Decimal(current_balance);
  } else {
    return new Decimal(0);
  }
}

// Sum of "will be paid off" balances at a given lien position (Debt page is the
// source). Lets the Loan Calculator and cash-to-close break the payoff total
// into Current 2nd TD / Current 3rd TD / other, WITHOUT double-counting: the
// three pieces always add back to balance_pay_offs_total.
export function payoff_balance_at_position(
  accountSummaries,
  residences,
  loan,
  position
) {
  const balances = accountSummaries.map(accountSummary => {
    const disposition =
      (loan && loan.account_dispositions[accountSummary.id]) || INCLUDE_IN_DTI;
    if (
      disposition !== WILL_BE_PAID_OFF ||
      accountSummary.lien_position !== position
    ) {
      return new Decimal(0);
    }
    return current_balance(
      accountSummary,
      residences[accountSummary.residence]
    );
  });
  return compute_total(balances);
}

// "Pay Off Debt" = the will-be-paid-off balances that are NOT the 1st/2nd/3rd
// mortgage liens (credit cards, other debt, a non-subject property loan) — the
// total minus the mortgage TDs, which are shown on their own Current Loan
// Balance 1st/2nd/3rd TD lines. Excludes the 1st so a refinanced 1st isn't
// listed twice (once as the 1st TD and again inside Pay Off Debt).
export function payoff_other_debt_total(accountSummaries, residences, loan) {
  return balance_pay_offs_total(accountSummaries, residences, loan)
    .sub(
      payoff_balance_at_position(
        accountSummaries,
        residences,
        loan,
        FIRST_POSITION
      )
    )
    .sub(
      payoff_balance_at_position(
        accountSummaries,
        residences,
        loan,
        SECOND_POSITION
      )
    )
    .sub(
      payoff_balance_at_position(
        accountSummaries,
        residences,
        loan,
        THIRD_POSITION
      )
    );
}

export function balance_pay_offs_total(accountSummaries, residences, loan) {
  const balancePayOffs = accountSummaries.map(accountSummary => {
    const currentBalance = current_balance(
      accountSummary,
      residences[accountSummary.residence]
    );
    const accountDisposition =
      (loan && loan.account_dispositions[accountSummary.id]) || INCLUDE_IN_DTI;
    return balance_pay_off(currentBalance, accountDisposition);
  });
  return compute_total(balancePayOffs);
}

// Real-estate-secured liability types (a property's own mortgages/HELOC). In the
// Equity From Sale Calculator the sold home's loans are already subtracted as
// the 1st/2nd TD balances, so counting them again in the debt payoff would
// double-count. This variant sums only NON-real-estate "will be paid off" debts
// (cars, cards, installment) that come out of the sale proceeds.
const REAL_ESTATE_SECURED_LIABILITY_TYPES = [
  PRIMARY_RESIDENCE,
  SECOND_HOME,
  SECOND_LOAN_SUB,
  HOME_EQUITY,
  MORTGAGE
];

export function non_real_estate_balance_pay_offs_total(
  accountSummaries,
  residences,
  loan
) {
  const balancePayOffs = accountSummaries
    .filter(
      accountSummary =>
        !REAL_ESTATE_SECURED_LIABILITY_TYPES.includes(
          accountSummary.liability_type
        )
    )
    .map(accountSummary => {
      const currentBalance = current_balance(
        accountSummary,
        residences[accountSummary.residence]
      );
      const accountDisposition =
        (loan && loan.account_dispositions[accountSummary.id]) || INCLUDE_IN_DTI;
      return balance_pay_off(currentBalance, accountDisposition);
    });
  return compute_total(balancePayOffs);
}

// Monthly payments of the NON-real-estate "will be paid off" debts (cards, cars,
// installment). Mirrors non_real_estate_balance_pay_offs_total. Used by the
// Summary Sheet "Existing Payments P/Off" line so the subject property's own
// mortgage payment — already shown on the "Existing Mo. Payment(s) P&I" line —
// isn't counted a second time.
export function non_real_estate_payment_savings_total(
  accountSummaries,
  residences,
  loan
) {
  const paymentSavings = accountSummaries
    .filter(
      accountSummary =>
        !REAL_ESTATE_SECURED_LIABILITY_TYPES.includes(
          accountSummary.liability_type
        )
    )
    .map(accountSummary =>
      payment_savings(
        accountSummary,
        residences[accountSummary.residence],
        (loan && loan.account_dispositions[accountSummary.id]) || INCLUDE_IN_DTI
      )
    );
  return compute_total(paymentSavings);
}

export function selected_balance_pay_offs_total(
  loans,
  summary,
  accountSummaries,
  residences
) {
  const loan = summary_selected_loan(loans, summary);
  if (!loan) {
    return new Decimal(0);
  }
  return balance_pay_offs_total(
    Object.values(accountSummaries),
    residences,
    loan
  );
}

export function payments_left(accountSummary, residence) {
  return current_balance(accountSummary, residence).div(
    monthly_payment(accountSummary, residence)
  );
}

// Calculations for the Reserve Calculator.
export function residence_total_payment(
  firstMortgage,
  secondMortgage,
  taxes,
  insurance,
  hoa
) {
  return [firstMortgage, secondMortgage, taxes, insurance, hoa]
    .map(value => {
      return new Decimal(value);
    })
    .reduce((total, value) => {
      return total.plus(value);
    }, new Decimal(0));
}

export function reserve_total_payment(reserves, residenceTotalPayment) {
  return new Decimal(reserves).times(new Decimal(residenceTotalPayment));
}

export function monthly_pitia_total(loans, investment_properties) {
  const isJumbo = loans.some(loan => loan.type === JUMBO);
  if (!isJumbo) {
    return new Decimal(0);
  }
  return investment_properties.reduce((accum, property) => {
    return accum.plus(total_payment(property));
  }, new Decimal(0));
}

export function pay_off_debt_to_qualify_minus_debt_to_income(
  documentType,
  borrowers,
  investmentProperties,
  accountSummaries,
  property,
  residences,
  loan,
  property_to_be_sold,
  is_selling_property
) {
  const totalMonthlyPayment = total_monthly_payment(
    documentType,
    loan,
    property,
    property_to_be_sold,
    is_selling_property
  );
  const totalPaymentForDTICalc = payment_for_DTI_calc_total(
    Object.values(accountSummaries),
    residences,
    investmentProperties,
    loan
  );
  const totalMonthlyIncome = year_to_payment(
    compute_dti_income(borrowers, investmentProperties),
    12
  );

  return totalMonthlyIncome.eq(0)
    ? new Decimal(0)
    : totalMonthlyPayment
      .plus(totalPaymentForDTICalc)
      .dividedBy(totalMonthlyIncome)
      .mul(100);
}

export function debt_to_income_ratio(
  documentType,
  borrowers,
  investmentProperties,
  accountSummaries,
  property,
  residences,
  loan,
  property_to_be_sold,
  is_selling_property
) {
  const totalMonthlyPayment = total_monthly_payment(
    documentType,
    loan,
    property,
    property_to_be_sold,
    is_selling_property
  );
  const totalPaymentForDTICalc = payment_for_DTI_calc_total(
    Object.values(accountSummaries),
    residences,
    investmentProperties,
    loan
  );
  const paymentSavingsTotal = payment_savings_total(
    Object.values(accountSummaries),
    residences,
    loan
  );
  const totalMonthlyIncome = year_to_payment(
    compute_dti_income(borrowers, investmentProperties),
    12
  );
  return totalMonthlyIncome.eq(0)
    ? new Decimal(0)
    : totalMonthlyPayment
      .plus(totalPaymentForDTICalc)
      .plus(paymentSavingsTotal)
      .dividedBy(totalMonthlyIncome)
      .mul(100);
}

export function revised_dti(
  documentType,
  pay_off_debt_to_qualify,
  borrowers,
  investmentProperties,
  accountSummaries,
  property,
  residences,
  loan,
  property_to_be_sold,
  is_selling_property
) {
  return pay_off_debt_to_qualify === NO
    ? debt_to_income_ratio(
      documentType,
      borrowers,
      investmentProperties,
      accountSummaries,
      property,
      residences,
      loan,
      property_to_be_sold,
      is_selling_property
    )
    : pay_off_debt_to_qualify_minus_debt_to_income(
      documentType,
      borrowers,
      investmentProperties,
      accountSummaries,
      property,
      residences,
      loan,
      property_to_be_sold,
      is_selling_property
    );
}
